import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  Baby, 
  Heart, 
  Smile, 
  Sparkles, 
  ShieldCheck, 
  Activity, 
  Zap, 
  AlertCircle,
  Stethoscope
} from "lucide-react";
import image1 from '../../Assets/Services/Pediatric5.jpg'
import image2 from '../../Assets/Services/Pediatric4.jpg'


const PediatricDentistry = () => {
  return (
    <ServiceLayout
      title="Pediatric Dentistry "
      subtitle="Gentle dental care for little smiles, building healthy habits for a lifetime."
      heroImage="/images/hero/pediatric-dentistry.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <p className="text-lg text-gray-700 leading-relaxed">
          Children need special care when it comes to their dental health. Paediatric dentistry (Pedodontics)
focuses on the oral health of infants, children, and adolescents, helping them develop strong,
healthy teeth and good dental habits from an early age. Early dental care is essential for proper
jaw development, healthy teeth, and a confident smile as they grow.
        </p>
        <p className="mt-4 text-gray-700">
          Comprehensive Paediatric Dental Services
        </p>
        <p className="mt-4 text-gray-700">
          We offer a wide range of gentle and child-friendly dental treatments designed to protect your child’s
teeth, support healthy development, and create confident smiles.
        </p>
      </section>

      {/* Importance of Early Care Grid */}
      <section className="bg-blue-50 p-8 rounded-3xl mb-12 border border-blue-100 shadow-sm">
        <h2 className="text-2xl font-bold text-blue-900 mb-8 flex items-center gap-2">
          <Heart className="text-blue-600" /> Importance of Early Dental Care
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <ShieldCheck className="text-blue-500" />, text: "Preventing tooth decay early" },
            { icon: <Activity className="text-blue-500" />, text: "Monitoring jaw and tooth growth" },
            { icon: <AlertCircle className="text-blue-500" />, text: "Early detection of dental problems" },
            { icon: <Sparkles className="text-blue-500" />, text: "Educating kids on oral hygiene" },
            { icon: <Smile className="text-blue-500" />, text: "Building positive dental habits" },
            { icon: <Baby className="text-blue-500" />, text: "Reducing fear of dental treatments" },
          ].map((benefit, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-xs">
              {benefit.icon}
              <span className="font-semibold text-gray-700 text-sm">{benefit.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services Categories */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Comprehensive Paediatric Services</h2>
        
        <div className="space-y-10">
          {/* Preventive Category */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <h4 className="text-xl font-bold text-blue-700 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" /> Preventive Care
              </h4>
              <p className="text-sm text-gray-500 mt-2">Protecting young teeth before problems start.</p>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Fluoride Treatments",
                "Cavity-preventing Sealants",
                "Professional Teeth Cleaning",
                "Oral Health Counseling"
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-gray-100 font-medium text-gray-700 text-sm">
                  <span className="text-blue-500">•</span> {s}
                </div>
              ))}
            </div>
          </div>

          {/* Restorative Category */}
          <div className="grid md:grid-cols-3 gap-6 border-t pt-10">
            <div className="md:col-span-1">
              <h4 className="text-xl font-bold text-blue-700 flex items-center gap-2">
                <Zap className="w-5 h-5" /> Restorative Care
              </h4>
              <p className="text-sm text-gray-500 mt-2">Saving damaged teeth with specialized techniques.</p>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Tooth-Colored Fillings",
                "Paediatric Root Canals (Pulp Therapy)",
                "Stainless Steel Crowns",
                "Space Maintainers"
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-gray-100 font-medium text-gray-700 text-sm">
                  <span className="text-blue-500">•</span> {s}
                </div>
              ))}
            </div>
          </div>

          {/* Habit Correction Category */}
          <div className="grid md:grid-cols-3 gap-6 border-t pt-10">
            <div className="md:col-span-1">
              <h4 className="text-xl font-bold text-blue-700 flex items-center gap-2">
                <Stethoscope className="w-5 h-5" /> Habit & Growth
              </h4>
              <p className="text-sm text-gray-500 mt-2">Correcting habits and guiding development.</p>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Thumb Sucking Correction",
                "Tongue Thrust Habit Treatment",
                "Early Bite Correction",
                "Dental Trauma Management"
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-gray-100 font-medium text-gray-700 text-sm">
                  <span className="text-blue-500">•</span> {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Ribbon */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-12 shadow-xl text-center">
        <h2 className="text-2xl font-bold mb-6 !text-blue-300">Child-Friendly Dental Environment</h2>
        <p className="max-w-3xl mx-auto !text-blue-100 leading-relaxed">
          Our pediatric dental department is designed to create a comfortable, welcoming, and stress-free
experience for children.
        </p>
        <p className="max-w-3xl mx-auto !text-blue-100 leading-relaxed">
          Our specialists use gentle techniques, patience, and compassionate care to help young patients feel
relaxed during their visits. By creating positive dental experiences early in life, we help children
overcome fear and develop confidence in maintaining healthy smiles for years to come.
        </p>
      </section>

      {/* Conclusion */}
      <section className="border-t pt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Building Healthy Smiles for Children</h2>
        <p className="text-gray-700 leading-relaxed italic">
          Pediatric dentistry establishes lifelong oral health habits. Early care helps children maintain strong teeth, 
          healthy gums, and confident smiles. Our clinics in Vijayawada and Hyderabad are committed to providing 
          comprehensive dental care for children in a safe environment.
        </p>
      </section>
    </ServiceLayout>
  );
};

export default PediatricDentistry;